import React, { useState, useEffect, useRef } from 'react';
import { messagingService } from '../../../services/messagingService';
import { MessageCircle, Send, Loader2, X, Circle } from 'lucide-react';

export function MessagesTab() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineStatuses, setOnlineStatuses] = useState({});
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const conversationChannelRef = useRef(null);
  const userConversationsChannelRef = useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    loadConversations();
    setupUserConversationsSubscription();
    updateOnlineStatus('online');

    // Set user as offline when leaving
    return () => {
      updateOnlineStatus('offline');
      if (conversationChannelRef?.current) {
        messagingService?.unsubscribe(conversationChannelRef?.current);
      }
      if (userConversationsChannelRef?.current) {
        messagingService?.unsubscribe(userConversationsChannelRef?.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation?.id);
      markAsRead(selectedConversation?.id);
      setupConversationSubscription(selectedConversation?.id);
    }

    return () => {
      if (conversationChannelRef?.current) {
        messagingService?.unsubscribe(conversationChannelRef?.current);
      }
    };
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const { data: { user } } = await messagingService?.supabase?.auth?.getUser();
      if (!user) return;
      
      setCurrentUserId(user?.id);
      const data = await messagingService?.getConversations(user?.id);
      setConversations(data);
      
      // Load online statuses
      const statuses = {};
      for (const conv of data) {
        const otherUserId = conv?.buyerId === user?.id ? conv?.supplier?.userId : conv?.buyerId;
        if (otherUserId) {
          const status = await messagingService?.getUserOnlineStatus(otherUserId);
          statuses[otherUserId] = status?.status || 'offline';
        }
      }
      setOnlineStatuses(statuses);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const data = await messagingService?.getMessages(conversationId);
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const markAsRead = async (conversationId) => {
    try {
      const { data: { user } } = await messagingService?.supabase?.auth?.getUser();
      if (!user) return;
      await messagingService?.markMessagesAsRead(conversationId, user?.id);
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };

  const setupConversationSubscription = (conversationId) => {
    conversationChannelRef.current = messagingService?.subscribeToConversation(
      conversationId,
      {
        onNewMessage: (message) => {
          setMessages((prev) => [...prev, message]);
          markAsRead(conversationId);
        },
        onMessageUpdate: (message) => {
          setMessages((prev) =>
            prev?.map((m) => (m?.id === message?.id ? message : m))
          );
        },
        onTypingChange: async () => {
          const typing = await messagingService?.getTypingUsers(conversationId);
          setTypingUsers(typing?.filter(t => t?.userId !== currentUserId) || []);
        }
      }
    );
  };

  const setupUserConversationsSubscription = async () => {
    try {
      const { data: { user } } = await messagingService?.supabase?.auth?.getUser();
      if (!user) return;

      userConversationsChannelRef.current = messagingService?.subscribeToUserConversations(
        user?.id,
        {
          onConversationUpdate: () => {
            loadConversations();
          },
          onUnreadCountChange: () => {
            loadConversations();
          }
        }
      );
    } catch (error) {
      console.error('Failed to setup user conversations subscription:', error);
    }
  };

  const updateOnlineStatus = async (status) => {
    try {
      await messagingService?.updateOnlineStatus(status);
    } catch (error) {
      console.error('Failed to update online status:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await messagingService?.sendMessage({
        conversationId: selectedConversation?.id,
        content: newMessage?.trim()
      });
      setNewMessage('');
      handleTyping(false);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleTyping = (isTyping) => {
    if (!selectedConversation) return;

    if (typingTimeoutRef?.current) {
      clearTimeout(typingTimeoutRef?.current);
    }

    messagingService?.setTypingStatus(selectedConversation?.id, isTyping);

    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        messagingService?.setTypingStatus(selectedConversation?.id, false);
      }, 3000);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getOtherParticipant = (conversation) => {
    if (!currentUserId || !conversation) return null;
    
    if (conversation?.buyerId === currentUserId) {
      return {
        name: conversation?.supplier?.nameEn || 'Supplier',
        avatar: conversation?.supplier?.logoUrl,
        userId: conversation?.supplier?.userId
      };
    } else {
      return {
        name: conversation?.buyer?.fullName || 'Buyer',
        avatar: conversation?.buyer?.avatarUrl,
        userId: conversation?.buyerId
      };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-200px)] bg-white rounded-lg shadow-md overflow-hidden">
      {/* Conversations List */}
      <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Messages
          </h2>
        </div>
        
        {conversations?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No conversations yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {conversations?.map((conversation) => {
              const participant = getOtherParticipant(conversation);
              const isOnline = onlineStatuses?.[participant?.userId] === 'online';
              
              return (
                <div
                  key={conversation?.id}
                  onClick={() => setSelectedConversation(conversation)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedConversation?.id === conversation?.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      {participant?.avatar ? (
                        <img
                          src={participant?.avatar}
                          alt={participant?.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-semibold">
                            {participant?.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <Circle
                        className={`w-3 h-3 absolute bottom-0 right-0 ${
                          isOnline ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-gray-400'
                        }`}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {participant?.name}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {formatTime(conversation?.lastMessageAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {conversation?.lastMessagePreview}
                      </p>
                      {conversation?.bean && (
                        <p className="text-xs text-blue-600 mt-1 truncate">
                          Re: {conversation?.bean?.nameEn}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => {
                    const participant = getOtherParticipant(selectedConversation);
                    const isOnline = onlineStatuses?.[participant?.userId] === 'online';
                    
                    return (
                      <>
                        <div className="relative">
                          {participant?.avatar ? (
                            <img
                              src={participant?.avatar}
                              alt={participant?.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-semibold">
                                {participant?.name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                          )}
                          <Circle
                            className={`w-3 h-3 absolute bottom-0 right-0 ${
                              isOnline ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-gray-400'
                            }`}
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{participant?.name}</h3>
                          <p className="text-sm text-gray-500">
                            {isOnline ? 'Online' : 'Offline'}
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages?.map((message) => {
                const isOwn = message?.senderId === currentUserId;
                
                return (
                  <div
                    key={message?.id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        isOwn
                          ? 'bg-blue-600 text-white' :'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{message?.content}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`text-xs ${
                            isOwn ? 'text-blue-100' : 'text-gray-500'
                          }`}
                        >
                          {formatTime(message?.createdAt)}
                        </span>
                        {isOwn && (
                          <span className="text-xs text-blue-100">
                            {message?.status === 'read' ? '✓✓' : message?.status === 'delivered' ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Typing Indicator */}
              {typingUsers?.length > 0 && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-xs text-gray-500">typing...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e?.target?.value);
                    handleTyping(true);
                  }}
                  onBlur={() => handleTyping(false)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={!newMessage?.trim() || sending}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  Send
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}